import useCurrentUser from "@casino/legacy/client/hooks/user/useCurrentUser";
import useSite from "@casino/legacy/client/hooks/useSite";
import { useTournamentsCountQuery } from "@workspace/casino_core/queries/TournamentsCountQuery.client";
import type { TournamentsQueryVariables } from "@workspace/casino_core/queries/TournamentsQuery";
import {
  getTournamentsQueryClient,
  useTournamentsQuery,
} from "@workspace/casino_core/queries/TournamentsQuery.client";
import { useCallback, useEffect, useMemo, useState } from "react";
import useOnTournamentsChanged from "../useOnTournamentsChanged";
import getDisplayOrder from "./getDisplayOrder";
import type { TournamentsSectionState } from "./TournamentsSectionState";

type Props = {
  status?: TournamentStatus;
  first?: number;
  onlyMine?: boolean;
  excludeId?: string;
  gameId?: string;
  /** Server-rendered first page; the list is seeded from it instead of fetched on mount. */
  initial?: TournamentsSectionState;
};

const useTournaments = ({
  status,
  first,
  onlyMine,
  excludeId,
  gameId,
  initial,
}: Props) => {
  const { site } = useSite();
  const { user } = useCurrentUser();
  const variables: TournamentsQueryVariables = useMemo(
    () => ({
      site,
      onlyCurrent: status === "current" && true,
      onlyFuture: status === "future" && true,
      onlyPast: status === "past" && true,
      onlyMy: onlyMine,
      gameId,
    }),
    [site, status, onlyMine, gameId],
  );
  const listVariables: TournamentsQueryVariables = useMemo(
    () => ({
      ...variables,
      first: first ?? 7,
      orderBy: getDisplayOrder(status),
      excludeTournamentIds: excludeId,
    }),
    [variables, first, status, excludeId],
  );

  // The server-rendered page was fetched without a session (it is cached
  // across users), so a logged-in user still fetches on mount to get
  // `iAmOptedIn` and `myScore`.
  const skip = initial?.tournaments !== undefined && !user?.id;

  const {
    data: countData,
    loading: loadingCount,
    refetch: refetchCount,
  } = useTournamentsCountQuery({ variables, skip });
  const { data, loading, error, refetch } = useTournamentsQuery({
    variables: listVariables,
    skip,
  });

  const [edges, setEdges] = useState(initial?.tournaments?.edges);
  const [pageInfo, setPageInfo] = useState(initial?.tournaments?.pageInfo);
  const [totalCount, setTotalCount] = useState(initial?.totalCount);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (data) {
      const tournaments = data.casinoTournamentsPublic?.tournaments;
      setEdges(tournaments?.edges);
      setPageInfo(tournaments?.pageInfo);
    }
  }, [data]);

  useEffect(() => {
    if (countData) {
      setTotalCount(countData.casinoTournamentsPublic?.tournamentsCount);
    }
  }, [countData]);

  useOnTournamentsChanged(() => {
    refetch();
    refetchCount();
  });

  const endCursor = pageInfo?.endCursor;

  const onLoadMore = useCallback(async () => {
    setLoadingMore(true);
    const { data: nextPage } = await getTournamentsQueryClient({
      ...variables,
      first: 15,
      orderBy: getDisplayOrder(status),
      excludeTournamentIds: excludeId,
      after: endCursor,
    });
    const tournaments = nextPage?.casinoTournamentsPublic?.tournaments;
    if (tournaments) {
      setEdges((previous) => [
        ...(previous ?? []),
        ...(tournaments.edges ?? []),
      ]);
      setPageInfo(tournaments.pageInfo);
    }
    setLoadingMore(false);
  }, [endCursor, variables, excludeId, status]);

  return {
    loading:
      (loading && edges === undefined) ||
      (loadingCount && totalCount === undefined) ||
      loadingMore,
    error,
    tournaments: edges?.filter((edge) => edge != null),
    totalCount,
    onLoadMore,
    hasNextPage: pageInfo?.hasNextPage,
  };
};

export default useTournaments;
